import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useRef, useState, type ForwardedRef, type ReactNode } from 'react';
import { Button, type ButtonProps } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { IconButton } from '../../components/IconButton';
import Modal from '../../components/Modal';
import LinkButton from '../../components/LinkButton';
import styles from './CookieBanner.module.css';
import LogoCompact from './LogoCompact';
function RejectButton(t0) {
  const $ = _c(8);
  let buttonProps;
  let children;
  let onClick;
  if ($[0] !== t0) {
    ({
      onClick,
      children,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = children;
    $[3] = onClick;
  } else {
    buttonProps = $[1];
    children = $[2];
    onClick = $[3];
  }
  let t1;
  if ($[4] !== buttonProps || $[5] !== children || $[6] !== onClick) {
    t1 = <Button onClick={onClick} {...buttonProps} kind="secondary" ghost={true}>{children}</Button>;
    $[4] = buttonProps;
    $[5] = children;
    $[6] = onClick;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
const AcceptButton = forwardRef(function AcceptButton(t0, ref) {
  const $ = _c(9);
  let children;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = props;
  } else {
    children = $[1];
    onClick = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== onClick || $[6] !== props || $[7] !== ref) {
    t1 = <IconButton {...props} onClick={onClick} iconName="check" kind="secondary" ghost={true} ref={ref}>{children}</IconButton>;
    $[4] = children;
    $[5] = onClick;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
});
export type CookieBannerProps = {
  text: {
    headline: string;
    introductionText: string;
    callForAction: string;
    informationHeadline: string;
    informationDetailsContent: ReactNode;
    rejectButtonLabel: string;
    acceptButtonLabel: string;
    informationButtonLabel: string;
    closeButtonLabel: string;
  };
  onRejectTracking: () => void;
  onAcceptTracking: () => void;
};
function useAnimateOnMount() {
  const $ = _c(2);
  const [state, setState] = useState("initial");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      requestAnimationFrame(() => setState("open"));
      return () => {
        setState("initial");
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return state;
}
const CookieBanner = forwardRef(function CookieBanner(t0, ref) {
  const $ = _c(47);
  const {
    onAcceptTracking,
    onRejectTracking,
    text: t1
  } = t0;
  const {
    headline,
    introductionText,
    callForAction,
    informationHeadline,
    informationButtonLabel,
    rejectButtonLabel,
    acceptButtonLabel,
    closeButtonLabel,
    informationDetailsContent
  } = t1;
  const focusRef = useRef(null);
  const [showCookiesModal, setShowCookiesModal] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setShowCookiesModal(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const openModal = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setShowCookiesModal(false);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const closeModal = t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!focusRef.current) {
        return;
      }
      focusRef.current.focus();
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  const state = useAnimateOnMount();
  const t6 = styles[state];
  let t7;
  if ($[4] !== t6) {
    t7 = classNames(styles.cookieBannerContainer, t6);
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      height: "100%"
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={styles.logoWrapper}><div className={styles.logo} style={t8}><LogoCompact style={{
          width: 135
        }} /></div></div>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== headline) {
    t10 = <Heading as="h2" level="heading-sm">{headline}</Heading>;
    $[8] = headline;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== introductionText) {
    t11 = <p>{introductionText}</p>;
    $[10] = introductionText;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== callForAction) {
    t12 = <p>{callForAction}</p>;
    $[12] = callForAction;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== informationButtonLabel) {
    t13 = <div className={styles.moreInformationWrapper}><LinkButton onClick={openModal} kind="onColor" type="button">{informationButtonLabel}</LinkButton></div>;
    $[14] = informationButtonLabel;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== onRejectTracking || $[17] !== rejectButtonLabel) {
    t14 = <RejectButton onClick={onRejectTracking}>{rejectButtonLabel}</RejectButton>;
    $[16] = onRejectTracking;
    $[17] = rejectButtonLabel;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== acceptButtonLabel || $[20] !== onAcceptTracking) {
    t15 = <AcceptButton onClick={onAcceptTracking} ref={focusRef} className={styles.acceptButton}>{acceptButtonLabel}</AcceptButton>;
    $[19] = acceptButtonLabel;
    $[20] = onAcceptTracking;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t14 || $[23] !== t15) {
    t16 = <div className={styles.cookieActionButtonsContainer}>{t14}{t15}</div>;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t13 || $[26] !== t16) {
    t17 = <div className={styles.cookieBannerButtonRow}>{t13}{t16}</div>;
    $[25] = t13;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t17) {
    t18 = <div className={styles.cookieContentContainer}><div className={styles.row}>{t9}<div className={styles.textContent}>{t10}{t11}{t12}{t17}</div></div></div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== ref || $[34] !== t18 || $[35] !== t7) {
    t19 = <div id="cookie-banner" ref={ref} className={t7}>{t18}</div>;
    $[33] = ref;
    $[34] = t18;
    $[35] = t7;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== informationDetailsContent) {
    t20 = <div className={styles.informationDetailsContent}>{informationDetailsContent}</div>;
    $[37] = informationDetailsContent;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== closeButtonLabel || $[40] !== informationHeadline || $[41] !== showCookiesModal || $[42] !== t20) {
    t21 = <Modal isOpen={showCookiesModal} onClose={closeModal} title={informationHeadline} hideActions={true} closeText={closeButtonLabel} fullWidth={true}>{t20}</Modal>;
    $[39] = closeButtonLabel;
    $[40] = informationHeadline;
    $[41] = showCookiesModal;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t19 || $[45] !== t21) {
    t22 = <>{t19}{t21}</>;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  return t22;
});
export default CookieBanner;